/* AJH page transition
   ================================================================
   Pairs with ajh-page-transition.css and the inline pre-paint script
   in theme.liquid <head>.

   Four jobs:
     1. Promote 'ajh-page-entering' -> 'ajh-page-entered' one frame
        after DOMContentLoaded so the page-level CSS fade can run.
     2. Stage the reveal of <img> and <video> inside #MainContent so
        text/layout appears first and each media element fades in
        only when its pixels are actually ready (or when the load
        event fires, on still-loading images).
     3. Intercept clicks on internal links anywhere on the site;
        fade out, then navigate, so no front-end route change ever
        lands on a blank/unstyled page. Private Viewing Room pages
        (identified server-side by template_suffix, listed in
        window.AJH_PVR_URLS) are excluded on both ends — entering or
        leaving one of these never triggers this fade, so the PVR's
        own internal transitions are left completely alone.
     4. Clean up classes when Safari restores the page from bfcache,
        so a back-button press never leaves the page faded out.
*/
(function () {
  var TRANSITION_OUT_MS  = 320;  // page-level fade-out duration
  var ENTERED_HOLD_MS    = 600;  // long enough for the 350ms fade-in to finish
  var TEXT_LEAD_MS       = 250;  // delay before any image starts appearing
  var STAGGER_MS         = 60;   // small stagger between cached images
  var MEDIA_MAX_WAIT_MS  = 8000; // hard cap: never leave media hidden longer

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── 1. Page-level entering -> entered transition ─────────────────
  function startEnteringTransition() {
    if (!document.documentElement.classList.contains('ajh-page-entering')) return;
    // Two RAFs so the browser commits the opacity:0 paint before we transition.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        // Kick off staged media reveal BEFORE we drop 'entering', so the
        // CSS hide rule keeps continuity with the new 'staging' class.
        stageMediaReveal();
        document.documentElement.classList.remove('ajh-page-entering');
        document.documentElement.classList.add('ajh-page-entered');
        setTimeout(function () {
          document.documentElement.classList.remove('ajh-page-entered');
        }, ENTERED_HOLD_MS);
      });
    });
  }

  // ── 2. Staged media reveal ───────────────────────────────────────
  function stageMediaReveal() {
    // Only collect eager-loaded media. Lazy images are below-the-fold
    // by intent; let them appear naturally as the user scrolls.
    var media = Array.prototype.slice.call(
      document.querySelectorAll('#MainContent img, #MainContent video')
    ).filter(function (el) {
      return el.loading !== 'lazy';
    });

    if (media.length === 0) return;

    document.documentElement.classList.add('ajh-page-staging');

    var pending = media.length;
    var staggerIndex = 0;

    function markReady(el, delay) {
      setTimeout(function () {
        if (el.classList.contains('ajh-img-ready')) return;
        el.classList.add('ajh-img-ready');
        pending--;
        if (pending <= 0) {
          document.documentElement.classList.remove('ajh-page-staging');
        }
      }, delay || 0);
    }

    media.forEach(function (el) {
      var isLoaded;
      if (el.tagName === 'IMG') {
        isLoaded = el.complete && el.naturalHeight > 0;
      } else { // VIDEO
        isLoaded = el.readyState >= 2; // HAVE_CURRENT_DATA
      }

      if (isLoaded) {
        // Cached / already painted: reveal after text lead time + stagger
        markReady(el, TEXT_LEAD_MS + (staggerIndex++ * STAGGER_MS));
      } else {
        // Still loading: reveal when ready, never before lead time
        var startedAt = Date.now();
        var fired = false;
        var reveal = function () {
          if (fired) return;
          fired = true;
          var elapsed = Date.now() - startedAt;
          var leadDelay = Math.max(0, TEXT_LEAD_MS - elapsed);
          markReady(el, leadDelay);
        };
        el.addEventListener('load',       reveal, { once: true });
        el.addEventListener('error',      reveal, { once: true });
        if (el.tagName === 'VIDEO') {
          el.addEventListener('loadeddata', reveal, { once: true });
        }
        // Hard fallback: don't ever leave media hidden indefinitely
        setTimeout(reveal, MEDIA_MAX_WAIT_MS);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startEnteringTransition);
  } else {
    startEnteringTransition();
  }

  // ── 3. Intercept internal navigation clicks (site-wide) ──────────
  // window.AJH_PVR_URLS is the authoritative list of Private Viewing
  // Room page URLs, built server-side in theme.liquid from each page's
  // real template_suffix. We check against this list rather than
  // guessing from the URL slug, since a page's handle (e.g. /pages/vanitas)
  // doesn't have to contain 'pvr' even when its template does.
  var PVR_URLS = (window.AJH_PVR_URLS || []);

  function isPvrUrl(pathname) {
    return PVR_URLS.indexOf(pathname) !== -1;
  }

  function isOnPvrPage() {
    return isPvrUrl(window.location.pathname);
  }

  if (!reduceMotion) {
    document.addEventListener('click', function (e) {
      // Don't interfere with new-tab/window or non-primary clicks.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.button !== undefined && e.button !== 0) return;
      if (e.defaultPrevented) return;

      var link = e.target && e.target.closest && e.target.closest('a[href]');
      if (!link) return;
      if (link.target === '_blank') return;
      if (link.hasAttribute('download')) return;

      var url;
      try { url = new URL(link.href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;

      // Same-page anchors (e.g. '#section') and same-path links should
      // navigate/scroll natively; nothing to fade between.
      if (url.pathname === window.location.pathname && url.hash) return;

      // Don't intercept utility/quick-add forms inside cards, or any
      // link explicitly opted out via data-no-page-transition.
      if (link.closest('product-form, [data-product-form], [data-no-page-transition]')) return;

      // Private Viewing Rooms are excluded on both ends: never fade
      // when leaving a PVR page, and never fade when heading into one.
      // The PVR has its own internal transition language; this system
      // should never touch it.
      if (isOnPvrPage()) return;
      if (isPvrUrl(url.pathname)) return;

      e.preventDefault();
      try { sessionStorage.setItem('ajh-page-fade-in', '1'); } catch (err) {}
      document.documentElement.classList.add('ajh-page-leaving');

      setTimeout(function () {
        window.location.href = link.href;
      }, TRANSITION_OUT_MS);
    });
  }

  // ── 4. bfcache restore (Safari/Chrome back button) ───────────────
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      var c = document.documentElement.classList;
      c.remove('ajh-page-leaving');
      c.remove('ajh-page-entering');
      c.remove('ajh-page-entered');
      c.remove('ajh-page-staging');
    }
  });
})();
